import React, { useState, useEffect, useRef } from "react";
import {
  Upload,
  X,
  ChevronDown,
  ChevronRight,
  FileText,
  Image,
  File,
  FileEdit,
  Plus,
} from "lucide-react";
import { useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  Alert,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Paper,
  Modal,
  Box,
  Snackbar,
} from "@mui/material";
import { getToken, base_url } from "../config/config";

const AnfrageUploader = () => {
  // Wir erwarten, dass die URL z. B. /anfrageUploader/70001 die Anfrage‑Nummer (ANF) enthält
  const { anf } = useParams();

  // Zustände für Kategorien, Labels, Dokumente, etc.
  const [categories, setCategories] = useState({});
  const [categoryIds, setCategoryIds] = useState({});
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newLabelName, setNewLabelName] = useState("");
  const [newSubCategoryName, setNewSubCategoryName] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [labels, setLabels] = useState([]);

  const fileInputRef = useRef(null);

  const SUPPORTED_IMAGE_EXTENSIONS = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "bmp",
    "webp",
    "tiff",
    "svg",
    "ico",
    "heic",
    "heif",
    "avif",
    "apng",
  ];

  const token = getToken();

  useEffect(() => {
    fetchCategories();
    fetchAllLabels();
  }, []);

  // Alle Labels laden
  const fetchAllLabels = async () => {
    try {
      const response = await fetch(`${base_url}/v1/getAllLabels`, {
        headers: { Authorization: `Bearer ${token}` },
        method: "GET",
        redirect: "follow",
      });
      const result = await response.json();
      setLabels(result);
    } catch (err) {
      console.error("Fehler beim Laden der Labels:", err);
      setError("Labels konnten nicht geladen werden.");
    }
  };

  // Kategorien (bzw. Unterkategorien) laden
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${base_url}/v1/getAllSubCategories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      const organizedData = {};
      const idMapping = {};

      data.forEach((item) => {
        const categoryName = item.labelId.name;
        if (!organizedData[categoryName]) {
          organizedData[categoryName] = [];
        }
        organizedData[categoryName].push(item.name);
        idMapping[item.name] = item._id;
      });

      setCategories(organizedData);
      setCategoryIds(idMapping);
    } catch (err) {
      console.error("Fehler beim Laden der Kategorien:", err);
      setError("Kategorien konnten nicht geladen werden.");
    }
  };

  // Neues Label erstellen
  const handleCreateLabel = async () => {
    try {
      const response = await fetch(`${base_url}/v1/createLabel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: newLabelName }),
      });

      if (!response.ok) throw new Error("Label-Erstellung fehlgeschlagen");

      setSuccess("Label erfolgreich erstellt.");
      setNewLabelName("");
      fetchCategories();
    } catch (err) {
      console.error("Fehler beim Erstellen des Labels:", err);
      setError("Label konnte nicht erstellt werden.");
    }
  };

  // Neue Unterkategorie erstellen
  const handleCreateSubCategory = async () => {
    if (!selectedLabel) {
      setError("Bitte wählen Sie zuerst ein Label aus.");
      return;
    }

    const selectedLabelData = labels.find(
      (label) => label.name === selectedLabel,
    );
    if (!selectedLabelData) {
      setError("Das ausgewählte Label wurde nicht gefunden.");
      return;
    }

    try {
      const response = await fetch(`${base_url}/v1/createSubCategory`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newSubCategoryName,
          labelId: selectedLabelData._id,
        }),
      });

      if (!response.ok)
        throw new Error("Erstellung der Unterkategorie fehlgeschlagen");

      setSuccess("Unterkategorie erfolgreich erstellt.");
      setNewSubCategoryName("");
      setSelectedLabel("");
      fetchCategories();
    } catch (err) {
      console.error("Fehler beim Erstellen der Unterkategorie:", err);
      setError("Unterkategorie konnte nicht erstellt werden.");
    }
  };

  // Prüft, ob eine Datei ein Bild ist
  const isImageFile = (file) => {
    const extension = file.name.split(".").pop().toLowerCase();
    return (
      SUPPORTED_IMAGE_EXTENSIONS.includes(extension) ||
      file.type.startsWith("image/")
    );
  };

  // Erzeugt einen Vorschaueintrag für gängige Dateitypen
  const getFilePreview = async (file) => {
    if (isImageFile(file)) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve({ type: "image", url: e.target.result });
        reader.readAsDataURL(file);
      });
    }

    if (
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel"
    ) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const workbook = XLSX.read(e.target.result, { type: "array" });
          const firstSheet = workbook.SheetNames[0];
          const preview = XLSX.utils
            .sheet_to_json(workbook.Sheets[firstSheet], { header: 1 })
            .slice(0, 5);
          resolve({ type: "excel", preview });
        };
        reader.readAsArrayBuffer(file);
      });
    }

    if (file.type === "application/pdf") {
      return { type: "pdf" };
    }

    if (
      file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      file.name.toLowerCase().endsWith(".docx")
    ) {
      return { type: "word" };
    }

    // Für andere Dateitypen
    return { type: "file" };
  };

  // Kategorien-Sidebar umschalten (auf/zu)
  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    newExpanded.has(category)
      ? newExpanded.delete(category)
      : newExpanded.add(category);
    setExpandedCategories(newExpanded);
  };

  // Beim Starten des Drag-&‑Drop-Vorgangs für ein Label
  const handleLabelDragStart = (e, label) => {
    e.dataTransfer.setData("text/plain", label);
    e.dataTransfer.effectAllowed = "copy";
  };

  // Dateien per Drag-&‑Drop in den Upload-Bereich ablegen
  const handleDocumentDrop = async (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("bg-blue-50");
    const files = Array.from(e.dataTransfer.files);

    for (const file of files) {
      const preview = await getFilePreview(file);
      setDocuments((prev) => [
        ...prev,
        {
          id: Date.now() + Math.random(),
          name: file.name,
          file,
          preview,
          labels: [],
        },
      ]);
    }
  };

  // Beim Ablegen eines Labels auf ein Dokument
  const handleLabelDrop = (e, docId) => {
    e.preventDefault();
    e.currentTarget.classList.remove("bg-blue-50");
    const label = e.dataTransfer.getData("text/plain");

    setDocuments((docs) =>
      docs.map((doc) => {
        if (doc.id === docId && !doc.labels.includes(label)) {
          return { ...doc, labels: [...doc.labels, label] };
        }
        return doc;
      }),
    );
  };

  // Dateien über die API hochladen – für jedes Dokument und jedes zugeordnete Label
  const handleSubmit = async () => {
    setLoading(true);
    try {
      for (const doc of documents) {
        if (doc.labels.length > 0) {
          for (const label of doc.labels) {
            const formData = new FormData();
            formData.append("document", doc.file);

            const categoryId = categoryIds[label];
            if (!categoryId) {
              console.error(`Keine Kategorie-ID für Label gefunden: ${label}`);
              continue;
            }
            await fetch(
              `${base_url}/v1/upload/documentToAnfrage?ANF=${anf}&categoryId=${categoryId}`,
              {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
              },
            );
          }
        }
      }
      setDocuments([]);
      setSuccess("Dokumente wurden erfolgreich hochgeladen.");
    } catch (err) {
      console.error("Fehler beim Hochladen:", err);
      setError("Dokumente konnten nicht hochgeladen werden.");
    }
    setLoading(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add("bg-blue-50");
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove("bg-blue-50");
  };

  const removeDocument = (docId) => {
    setDocuments((docs) => docs.filter((doc) => doc.id !== docId));
  };

  const removeLabel = (docId, labelToRemove) => {
    setDocuments((docs) =>
      docs.map((doc) => {
        if (doc.id === docId) {
          return {
            ...doc,
            labels: doc.labels.filter((l) => l !== labelToRemove),
          };
        }
        return doc;
      }),
    );
  };

  // Rendert eine Dateivorschau je nach Dateityp
  const renderFilePreview = (doc) => {
    switch (doc.preview.type) {
      case "image":
        return (
          <img
            src={doc.preview.url}
            alt={doc.name}
            className='w-full h-48 object-cover rounded'
          />
        );
      case "excel":
        return (
          <div className='w-full h-48 overflow-auto bg-gray-50 p-2 rounded text-sm'>
            <table className='w-full'>
              <tbody>
                {doc.preview.preview.map((row, i) => (
                  <tr key={i} className='border-b'>
                    {row.map((cell, j) => (
                      <td key={j} className='p-1'>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case "pdf":
        return (
          <div className='w-full h-48 flex items-center justify-center bg-gray-50 rounded'>
            <FileText className='w-16 h-16 text-gray-400' />
          </div>
        );
      case "word":
        return (
          <div className='w-full h-48 flex items-center justify-center bg-gray-50 rounded'>
            <FileEdit className='w-16 h-16 text-gray-400' />
            <span className='ml-2'>Word-Dokument</span>
          </div>
        );
      case "file":
      default:
        return (
          <div className='w-full h-48 flex items-center justify-center bg-gray-50 rounded'>
            <File className='w-16 h-16 text-gray-400' />
          </div>
        );
    }
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
  };

  return (
    <div className='flex h-full bg-gray-100'>
      {/* Kategorien-Seitenleiste */}
      <Paper elevation={1} sx={{ width: 256, overflow: "auto", p: 2 }}>
        <div className='flex justify-between items-center mb-4'>
          <Typography variant='h6' component='h2'>
            Kategorien
          </Typography>
          <Button
            onClick={() => setShowCreateModal(true)}
            color='primary'
            sx={{ minWidth: "auto", p: 1 }}>
            <Plus />
          </Button>
        </div>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity='success' sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {Object.entries(categories).map(([category, subLabels]) => (
          <div key={category} className='mb-2'>
            <div
              className='flex items-center cursor-pointer hover:bg-gray-100 p-2 rounded'
              onClick={() => toggleCategory(category)}>
              {expandedCategories.has(category) ? (
                <ChevronDown className='w-4 h-4 mr-2' />
              ) : (
                <ChevronRight className='w-4 h-4 mr-2' />
              )}
              <Typography>{category}</Typography>
            </div>
            {expandedCategories.has(category) && (
              <div className='ml-4'>
                {subLabels.map((label) => (
                  <div
                    key={label}
                    className='p-2 hover:bg-blue-100 cursor-move rounded'
                    draggable
                    onDragStart={(e) => handleLabelDragStart(e, label)}>
                    {label}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </Paper>

      {/* Erstellungs-Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setError("");
          setSuccess("");
          setNewLabelName("");
          setNewSubCategoryName("");
          setSelectedLabel("");
        }}>
        <Box sx={modalStyle}>
          <Typography variant='h6' component='h3' sx={{ mb: 3 }}>
            Neues Label/Unterkategorie erstellen
          </Typography>
          {/* Neues Label */}
          <Box sx={{ mb: 3 }}>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Neues Label
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                value={newLabelName}
                onChange={(e) => setNewLabelName(e.target.value)}
                placeholder='Label-Name eingeben'
                size='small'
                fullWidth
              />
              <Button
                onClick={handleCreateLabel}
                variant='contained'
                color='primary'>
                Erstellen
              </Button>
            </Box>
          </Box>

          {/* Neue Unterkategorie */}
          <Box sx={{ mb: 3 }}>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Neue Unterkategorie
            </Typography>
            <FormControl fullWidth size='small' sx={{ mb: 1 }}>
              <InputLabel>Label auswählen</InputLabel>
              <Select
                value={selectedLabel}
                onChange={(e) => setSelectedLabel(e.target.value)}
                label='Label auswählen'>
                <MenuItem value=''>
                  <em>Keine</em>
                </MenuItem>
                {labels.map((label) => (
                  <MenuItem key={label._id} value={label.name}>
                    {label.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                value={newSubCategoryName}
                onChange={(e) => setNewSubCategoryName(e.target.value)}
                placeholder='Unterkategorie eingeben'
                size='small'
                fullWidth
              />
              <Button
                onClick={handleCreateSubCategory}
                variant='contained'
                color='primary'>
                Erstellen
              </Button>
            </Box>
          </Box>

          <Button
            onClick={() => {
              setShowCreateModal(false);
              setError("");
              setSuccess("");
              setNewLabelName("");
              setNewSubCategoryName("");
              setSelectedLabel("");
            }}
            variant='outlined'
            fullWidth>
            Schließen
          </Button>
        </Box>
      </Modal>

      {/* Dokumenten Upload-Bereich */}
      <Box sx={{ flex: 1, p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 6,
            textAlign: "center",
            border: "2px dashed",
            borderColor: "grey.300",
            "&:hover": { borderColor: "primary.main" },
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDocumentDrop}>
          <Upload sx={{ width: 48, height: 48, color: "grey.400", mb: 2 }} />
          <Typography color='textSecondary'>
            {loading
              ? "Wird hochgeladen..."
              : "Dateien hierher ziehen oder klicken, um auszuwählen"}
          </Typography>
          <Typography variant='body2' color='textSecondary' sx={{ mt: 1 }}>
            Unterstützt alle Bildformate (JPG, PNG, GIF usw.), Word-Dokumente,
            PDFs, Excel, AutoCAD-Dateien und mehr
          </Typography>
          <Button
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            variant='contained'
            color='primary'
            sx={{ mt: 2 }}>
            Dateien auswählen
          </Button>
          <input
            type='file'
            multiple
            ref={fileInputRef}
            onChange={async (e) => {
              const files = Array.from(e.target.files);
              for (const file of files) {
                const preview = await getFilePreview(file);
                setDocuments((prev) => [
                  ...prev,
                  {
                    id: Date.now() + Math.random(),
                    name: file.name,
                    file,
                    preview,
                    labels: [],
                  },
                ]);
              }
            }}
            style={{ display: "none" }}
          />
        </Paper>

        {/* Hochladen-Button */}
        {documents.length > 0 && (
          <Button
            onClick={handleSubmit}
            disabled={loading}
            variant='contained'
            color='primary'
            fullWidth
            sx={{ mb: 3 }}>
            {loading ? "Wird hochgeladen..." : "Gelabelte Dokumente hochladen"}
          </Button>
        )}

        {/* Dokumente-Gitter */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
            gap: 3,
          }}>
          {documents.map((doc) => (
            <Paper
              key={doc.id}
              sx={{ p: 2 }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleLabelDrop(e, doc.id)}>
              <Box sx={{ position: "relative" }}>
                {renderFilePreview(doc)}
                <Button
                  onClick={() => removeDocument(doc.id)}
                  color='error'
                  sx={{
                    minWidth: "auto",
                    p: 0.5,
                    position: "absolute",
                    top: 8,
                    right: 8,
                    borderRadius: "50%",
                  }}>
                  <X />
                </Button>
              </Box>
              <Typography
                variant='body2'
                sx={{
                  mt: 1,
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                {doc.name}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                {doc.labels.map((label) => (
                  <Box
                    key={label}
                    sx={{
                      bgcolor: "primary.50",
                      color: "primary.700",
                      px: 1,
                      py: 0.5,
                      borderRadius: "full",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "0.875rem",
                    }}>
                    {label}
                    <Button
                      onClick={() => removeLabel(doc.id, label)}
                      sx={{ ml: 0.5, minWidth: "auto", p: 0 }}>
                      <X sx={{ width: 16, height: 16 }} />
                    </Button>
                  </Box>
                ))}
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>

      {/* Erfolg-/Fehler-Snackbar */}
      <Snackbar
        open={Boolean(success || error)}
        autoHideDuration={6000}
        onClose={() => {
          setSuccess("");
          setError("");
        }}>
        <Alert
          severity={success ? "success" : "error"}
          onClose={() => {
            setSuccess("");
            setError("");
          }}>
          {success || error}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AnfrageUploader;
